import { useCallback, useMemo } from 'react';
import { Select, SelectOptionType } from '@octano/global-ui';
import { Control, useController } from 'react-hook-form';
import RemovableChip from '../chip/RemovableChip';

interface ChipMultiSelectProps {
  name: string;
  label: string;
  options: SelectOptionType[];
  control: Control<any>;
  placeholder?: string;
  rules?: Record<string, any>;
  hideChips?: boolean;
}

const ChipMultiSelect = ({
  name,
  label,
  options,
  control,
  placeholder,
  rules,
  hideChips = false,
}: ChipMultiSelectProps) => {
  const {
    field: { value = [], onChange },
  } = useController({ name, control, rules });

  const selectedValues = useMemo(
    () => (value as SelectOptionType[]).map((v) => v.value),
    [value],
  );

  const availableOptions = useMemo(
    () => options.filter((opt) => !selectedValues.includes(opt.value)),
    [options, selectedValues],
  );

  const handleAdd = useCallback(
    (option: SelectOptionType) => {
      const current = (value as SelectOptionType[]) || [];
      if (!current.some((v) => v.value === option.value)) {
        onChange([...current, option]);
      }
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      const current = [...((value as SelectOptionType[]) || [])];
      current.splice(index, 1);
      onChange(current);
    },
    [value, onChange],
  );

  return (
    <div>
      <Select
        name={`${name}_selector`}
        label={label}
        options={availableOptions}
        value={null as any}
        onChange={(e: any) => handleAdd(e)}
        placeholder={placeholder}
      />
      {!hideChips && (value as SelectOptionType[])?.length > 0 && (
        <div
          className="w-full mb-3 d-flex flex-wrap align-items-center justify-content-start"
          style={{ gap: 5 }}
        >
          {(value as SelectOptionType[]).map(
            (item: SelectOptionType, i: number) => (
              <RemovableChip
                key={String(item.value)}
                value={i}
                onRemove={handleRemove}
              >
                {item.label}
              </RemovableChip>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default ChipMultiSelect;
